import { AxisBottom, AxisLeft } from '@visx/axis';
import {
    Chart,
    Wrapper as DefaultWrapper,
} from 'components/DataVis/TimeSeriesChart';
import {
    xAxisTickFormat as defaultXAxisTickFormat,
    formatSeriesData,
    getPointerClickHandler,
    getPointerMoveHandler,
    yAxisTickFormat,
} from '../utils';
import { scaleLinear, scaleTime } from 'd3-scale';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DataSourceGlyphCircle } from '../../PopularityTimeSeries/tooltip-components';
import DefaultTooltip from './Tooltip';
import Drawer from 'components/Drawer';
import DriversOfGrowthDrawer from './DriversOfGrowthDrawer';
import { Group } from '@visx/group';
import { Line } from '@visx/shape';
import { ParentSize } from '@visx/responsive';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import PredictionLineSeries from './PredictionLineSeries';
import TickLabel from './TickLabel';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { timeYear } from 'd3-time';
import { useTooltip } from '@visx/tooltip';
import { voronoi } from '@visx/voronoi';
import { withMixpanel } from 'utils/app';

const X_AXIS_HEIGHT = 50;
const Y_AXIS_WIDTH = 70;
// Plot inset when showAxes=false; without it rounded line caps and the hover
// glyph clip at the SVG edge.
const AXISLESS_INSET = 8;

const Placeholder = styled.div.attrs(({ shimmerWidth = '10000px' }) => ({
    shimmerWidth,
}))`
    grid-area: chart;
    background: ${({ theme }) => theme.gray_300};
    min-height: ${({ height }) => `${height}px`};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius_sm};
    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : null)}
`;

function MultiTimeSeriesChart({
    data,
    width,
    height,
    setPredictionPercentage,
    tooltipProps,
    xAxisTickValues = timeYear,
    xAxisTickFormat = defaultXAxisTickFormat,
    Tooltip = DefaultTooltip,
    predictionStrokeDasharray = true,
    country,
    category,
    categoryDepth,
    brand,
    dataSource = 'tiktok',
    mixpanel,
    skipPrediction = false,
    showAxes = true,
    strokeWidth,
}) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPointData, setSelectedPointData] = useState(null);

    const {
        showTooltip,
        hideTooltip,
        tooltipData,
        tooltipLeft,
        tooltipTop,
        tooltipOpen,
    } = useTooltip({
        tooltipOpen: false,
        tooltipLeft: 0,
        tooltipTop: 0,
        tooltipData: null,
    });

    const hasDriversOfGrowthContext = Boolean(country && (category || brand));

    const trackingMeta = {
        dataSource,
        country,
        ...(brand
            ? { brand, type: 'brand' }
            : { category, categoryDepth, type: 'category' }),
    };

    const handlePointClick = useCallback(
        (pointData) => {
            if (!hasDriversOfGrowthContext) return;
            mixpanel?.track('Click', {
                context: 'DRIVERS_OF_GROWTH_DRAWER_OPEN',
                ...trackingMeta,
            });
            setSelectedPointData(pointData);
            setDrawerOpen(true);
        },
        [hasDriversOfGrowthContext, mixpanel, trackingMeta],
    );

    const lastTooltipTrackTime = useRef(0);
    const handleTooltipShow = useCallback(() => {
        const now = Date.now();
        if (now - lastTooltipTrackTime.current > 1000) {
            lastTooltipTrackTime.current = now;
            mixpanel?.track('Hover', {
                context: 'TIME_SERIES_TOOLTIP',
                ...trackingMeta,
            });
        }
    }, [mixpanel, trackingMeta]);

    const {
        accessors,
        xMin,
        xMax,
        yMin,
        yMax,
        predictionMinStartDate,
        formattedData,
    } = formatSeriesData(data, { skipPrediction });

    const insetLeft = showAxes ? Y_AXIS_WIDTH : AXISLESS_INSET;
    const insetRight = showAxes ? 20 : AXISLESS_INSET;
    const insetY = showAxes ? X_AXIS_HEIGHT : AXISLESS_INSET;

    const xScale = scaleTime()
        .domain([xMin, xMax])
        .range([insetLeft, width - insetRight]);
    const yScale = scaleLinear()
        .domain([yMin, yMax])
        .range([height - insetY, insetY]);
    const x = (d) => xScale(new Date(accessors.xAccessor(d)));
    const y = (d) => yScale(accessors.yAccessor(d));

    const voronoiInstance = voronoi({ x, y, width, height });
    const dataVoronoi = formattedData.map(({ historical, prediction }) =>
        voronoiInstance(
            historical.concat(prediction).filter((row) => {
                const date = dayjs(accessors.xAccessor(row)).toDate();
                return date >= xMin && date <= xMax;
            }),
        ),
    );

    useEffect(() => {
        if (width > 0)
            setPredictionPercentage(
                `${(xScale(predictionMinStartDate) / width) * 100}%`,
            );
    }, [width, xScale, predictionMinStartDate]);

    return (
        <div css="position: relative;">
            <svg width={width} height={height}>
                {showAxes && (
                    <AxisLeft
                        left={Y_AXIS_WIDTH}
                        scale={yScale}
                        orientation="left"
                        tickFormat={yAxisTickFormat}
                        tickComponent={TickLabel}
                        numTicks={3}
                        hideZero={true}
                        hideAxisLine={true}
                        hideTicks={true}
                    />
                )}
                {showAxes && (
                    <AxisBottom
                        orientation="bottom"
                        top={height - X_AXIS_HEIGHT}
                        scale={xScale}
                        tickValues={xAxisTickValues.range(xMin, xMax, 1)}
                        tickFormat={xAxisTickFormat}
                        tickComponent={TickLabel}
                        hideAxisLine={true}
                        hideTicks={true}
                    />
                )}
                {formattedData.map(
                    ({
                        id,
                        historical,
                        prediction,
                        color,
                        predictionColor,
                    }) => (
                        <PredictionLineSeries
                            key={id}
                            x={x}
                            y={y}
                            historical={historical}
                            prediction={prediction}
                            strokeColor={color}
                            strokeColorPrediction={predictionColor}
                            predictionStrokeDasharray={
                                predictionStrokeDasharray
                            }
                            strokeWidth={strokeWidth}
                        />
                    ),
                )}
                {tooltipOpen ? (
                    <Group>
                        <Line
                            from={{ x: tooltipData.x, y: 0 }}
                            to={{
                                x: tooltipData.x,
                                y: Math.max(height - insetY, 0),
                            }}
                            stroke="#3A3A3A"
                            strokeWidth={1}
                            strokeDasharray="2 2"
                        />
                        <DataSourceGlyphCircle
                            left={tooltipData.x}
                            top={tooltipData.y}
                            size={130}
                            css={`
                                fill: ${tooltipData.strokeColor};
                            `}
                        />
                    </Group>
                ) : null}
                <rect
                    {...(hasDriversOfGrowthContext && {
                        'data-component': `${dataSource}-growth-drivers`,
                    })}
                    x={insetLeft}
                    y={0}
                    width={Math.max(width - insetLeft, 0)}
                    height={Math.max(height - insetY, 0)}
                    fill="transparent"
                    style={{
                        cursor: hasDriversOfGrowthContext
                            ? 'pointer'
                            : 'default',
                    }}
                    onPointerMove={getPointerMoveHandler({
                        data: formattedData,
                        dataVoronoi,
                        tooltipData,
                        showTooltip,
                        x,
                        y,
                        onTooltipShow: handleTooltipShow,
                    })}
                    onPointerOut={() => hideTooltip()}
                    onPointerUp={() => hideTooltip()}
                    onClick={
                        hasDriversOfGrowthContext
                            ? getPointerClickHandler({
                                  data: formattedData,
                                  dataVoronoi,
                                  x,
                                  y,
                                  onClick: handlePointClick,
                              })
                            : undefined
                    }
                />
            </svg>
            <Tooltip
                left={tooltipLeft}
                top={tooltipTop}
                chartWidth={width}
                chartHeight={height}
                accessors={accessors}
                tooltipData={tooltipData}
                tooltipOpen={tooltipOpen}
                country={country}
                category={category}
                brand={brand}
                {...tooltipProps}
            />
            {hasDriversOfGrowthContext && (
                <Drawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    $width="42rem"
                >
                    <DriversOfGrowthDrawer
                        selectedPointData={selectedPointData}
                        setSelectedPointData={setSelectedPointData}
                        accessors={accessors}
                        country={country}
                        category={category}
                        categoryDepth={categoryDepth}
                        brand={brand}
                        dataSource={dataSource}
                        historicalData={formattedData[0]?.historical || []}
                    />
                </Drawer>
            )}
        </div>
    );
}

function MultiTimeSeriesChartOverview({
    children,
    data,
    chartHeight,
    isLoading,
    showPlaceholder,
    placeholderMessage,
    Wrapper = DefaultWrapper,
    country,
    category,
    categoryDepth,
    brand,
    dataSource = 'tiktok',
    mixpanel,
    skipPrediction = false,
    ...props
}) {
    const [predictionPercentage, setPredictionPercentage] = useState('72.1%');
    const maybeSetPredictionPercentage = useCallback(
        (nextPredictionPercentage) => {
            if (nextPredictionPercentage !== predictionPercentage) {
                setPredictionPercentage(nextPredictionPercentage);
            }
        },
        [setPredictionPercentage],
    );

    return (
        <Wrapper predictionStart={predictionPercentage}>
            {children}
            {showPlaceholder ? (
                <Placeholder height={chartHeight} isLoading={isLoading}>
                    {placeholderMessage}
                </Placeholder>
            ) : (
                <Chart as="div" height={chartHeight}>
                    <ParentSize>
                        {({ width, height }) => (
                            <MultiTimeSeriesChart
                                data={data}
                                width={width}
                                height={height}
                                setPredictionPercentage={
                                    maybeSetPredictionPercentage
                                }
                                country={country}
                                category={category}
                                categoryDepth={categoryDepth}
                                brand={brand}
                                dataSource={dataSource}
                                mixpanel={mixpanel}
                                skipPrediction={skipPrediction}
                                {...props}
                            />
                        )}
                    </ParentSize>
                </Chart>
            )}
        </Wrapper>
    );
}

export default withMixpanel(MultiTimeSeriesChartOverview);
